import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { FormHelperText } from '@mui/material';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import {
  fetchSignInMethodsForEmail,
  sendSignInLinkToEmail,
  signInAnonymously
} from 'firebase/auth';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { schema } from '../../api/firebase/auth/validation';
import { actionCodeSettings, auth } from '../../api/firebase/config';
import {
  createUserWhenSendemail,
  userFindByEmail
} from '../../api/firebase/firestore/user/index';
import {
  black,
  blackText,
  linkColor,
  primaryButtonBackground,
  primarycolor
} from '../../constants/Colors';
import {
  cardContentPadding,
  cardSize,
  textMb,
  titleMb,
  w100
} from '../../constants/Location';
import { screenType } from '../../constants/enum';
import { throwError } from '../../modules/error';

import CustomLoading from './CustomLoading';
import CustomSnackbar from './CustomSnackbar';
import CustomTextfield from './CustomTextfield';
import RegisterLinks from './RegisterLinks';

import type { RegisterScreenProps } from '../../api/firebase/auth/authType';

interface FormInputs {
  email: string;
  privacyCheck: boolean;
}

const styles = {
  CheckboxM: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    },
    marginBottom: '16px'
  },
  cardContentPadding,
  cardSize,
  required: {
    color: primarycolor,
    marginLeft: '10px'
  },
  textMb,
  textSmall: {
    fontSize: '11.5px'
  },
  titleMb,
  w100
};

const RegisterCard: React.FC<RegisterScreenProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors }
  } = useForm<FormInputs>({
    defaultValues: {
      privacyCheck: false
    },
    resolver: yupResolver(schema)
  });

  const checkboxHelper = (): string | undefined => {
    if (errors.privacyCheck === undefined) return;
    return String(errors.privacyCheck.message);
  };

  const [firstSubmit, setFirstSubmit] = useState(false);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('email') === undefined ||
        '' ||
        watch('privacyCheck') === undefined ||
        false ||
        Boolean(errors.email) ||
        Boolean(errors.privacyCheck)
      );
    }
    return false;
  };

  const [open, setOpen] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      const email = watch('email');

      const isSignIn: string[] = await fetchSignInMethodsForEmail(auth, email);

      if (isSignIn.length) {
        setErrorText(t('register.error.email'));
        setOpen(true);
      } else {
        const anonymous = await signInAnonymously(auth);

        if (anonymous) {
          const user = await userFindByEmail(email);

          if (!user) {
            await createUserWhenSendemail(props.screenType, email);
          }
          await sendSignInLinkToEmail(
            auth,
            email,
            actionCodeSettings(props.screenType, email)
          );
        } else {
          throw new Error();
        }

        if (props.screenType === screenType.client) {
          navigate('/client/auth/sent');
        } else if (props.screenType === screenType.agent) {
          navigate('/agent/auth/sent');
        } else {
          navigate('/publisher/auth/sent');
        }
      }
    } catch (e) {
      setErrorText(t('register.error.common'));
      setOpen(true);
      throwError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <Grid item xs={12}>
          <Card sx={styles.cardSize}>
            <CardContent sx={styles.cardContentPadding}>
              <Typography
                sx={[styles.titleMb, { color: blackText }]}
                component="div"
              >
                {props.screenType === screenType.client
                  ? t('type.client')
                  : props.screenType === screenType.agent
                  ? t('type.agent')
                  : t('type.publisher')}{' '}
                {t('register.title')}
              </Typography>
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    labelText={t('form.email')}
                    requiredText={t('form.required')}
                    isRequired
                    placeholder="xxx@en-broker.co.jp"
                  />
                )}
                control={control}
                name="email"
              />
              <Typography sx={styles.CheckboxM}>
                <Controller
                  render={({ field }) => (
                    <FormGroup>
                      <FormControlLabel
                        control={<Checkbox {...field} />}
                        sx={{ marginLeft: 0 }}
                        label={
                          <>
                            {props.screenType === screenType.client && (
                              <Link
                                sx={linkColor}
                                target="_blank"
                                href={'/main/common/client_term'}
                              >
                                {t('form.clientTerm')}
                              </Link>
                            )}
                            {props.screenType === screenType.agent && (
                              <Link
                                sx={linkColor}
                                target="_blank"
                                href={'/main/common/client_term'}
                              >
                                {t('form.agentTerm')}
                              </Link>
                            )}
                            {props.screenType === screenType.publisher && (
                              <Link
                                sx={linkColor}
                                target="_blank"
                                href={'/main/common/media_term'}
                              >
                                {t('form.mediaTerm')}
                              </Link>
                            )}
                            <Typography
                              component="span"
                              color={black}
                              sx={styles.textSmall}
                            >
                              、
                            </Typography>
                            <Link
                              sx={linkColor}
                              target="_blank"
                              href={'/main/common/privacy'}
                            >
                              {t('form.privacy')}
                            </Link>
                            <Typography
                              component="span"
                              color={black}
                              sx={styles.textSmall}
                            >
                              {t('form.agree')}
                            </Typography>
                          </>
                        }
                      />
                      <FormHelperText error={Boolean(errors.privacyCheck)}>
                        {checkboxHelper()}
                      </FormHelperText>
                    </FormGroup>
                  )}
                  control={control}
                  name="privacyCheck"
                />
              </Typography>
              <CardActions sx={styles.textMb}>
                <Grid container direction="column" alignItems="center">
                  <Button
                    disabled={buttonDisabledFlag()}
                    variant="contained"
                    fullWidth
                    sx={[
                      primaryButtonBackground,
                      {
                        '@media screen and (max-width:960px)': {
                          height: '42px'
                        }
                      }
                    ]}
                    onClick={() => {
                      void (async () => {
                        setFirstSubmit(true);
                        await handleSubmit(async () => await onSubmit())();
                      })();
                    }}
                  >
                    <Typography
                      component="div"
                      color="white"
                      sx={{ fontSize: 15 }}
                    >
                      {t('register.emailAuth')}
                    </Typography>
                  </Button>
                </Grid>
              </CardActions>
              <RegisterLinks screenType={props.screenType} />
            </CardContent>
          </Card>
          <CustomSnackbar
            open={open}
            autoHideDuration={6000}
            onClose={() => setOpen(false)}
            text={errorText}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default RegisterCard;
